/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the application.
 * Uses Firebase Auth with Google provider for authentication.
 */

import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  linkWithCredential,
  fetchSignInMethodsForEmail,
  type User as FirebaseUser,
  type AuthCredential,
} from 'firebase/auth';
import { doc, onSnapshot, setDoc, serverTimestamp, type Unsubscribe } from 'firebase/firestore';
import { getFirebaseAuth, getFirebaseDb } from '@/lib/firebase';
import type { User } from '@flash/shared';

/**
 * Pending account link information
 */
export interface PendingLinkInfo {
  /** Email that has a conflict */
  email: string;
  /** Credential that needs to be linked after signing in with existing method */
  credential: AuthCredential;
  /** Existing sign-in methods for this email */
  existingMethods: string[];
}

/**
 * Auth context value interface
 */
export interface AuthContextValue {
  /** Current Firebase user (null if not authenticated) */
  user: FirebaseUser | null;
  /** Current user's Firestore document (null if not loaded) */
  userProfile: User | null;
  /** Whether auth state is being determined */
  loading: boolean;
  /** Auth error message (null if no error) */
  error: string | null;
  /** Whether the current user is a platform admin */
  isAdmin: boolean;
  /** Pending account link info when there's a credential conflict */
  pendingLink: PendingLinkInfo | null;
  /** Sign in with Google popup */
  signIn: () => Promise<void>;
  /** Sign in with email and password */
  signInWithEmail: (email: string, password: string) => Promise<void>;
  /** Sign up with email and password */
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  /** Sign out the current user */
  signOut: () => Promise<void>;
  /** Clear any auth errors */
  clearError: () => void;
  /** Link pending credential to current user */
  linkPendingCredential: () => Promise<void>;
  /** Cancel pending link operation */
  cancelPendingLink: () => void;
  /** Get sign-in methods for an email */
  getSignInMethodsForEmail: (email: string) => Promise<string[]>;
  /** Mark the current user as onboarded */
  markAsOnboarded: () => Promise<void>;
}

/**
 * Default context value (used when context is accessed outside provider)
 */
const defaultContextValue: AuthContextValue = {
  user: null,
  userProfile: null,
  loading: true,
  error: null,
  isAdmin: false,
  pendingLink: null,
  signIn: async () => {
    throw new Error('AuthProvider not found');
  },
  signInWithEmail: async () => {
    throw new Error('AuthProvider not found');
  },
  signUpWithEmail: async () => {
    throw new Error('AuthProvider not found');
  },
  signOut: async () => {
    throw new Error('AuthProvider not found');
  },
  clearError: () => {
    throw new Error('AuthProvider not found');
  },
  linkPendingCredential: async () => {
    throw new Error('AuthProvider not found');
  },
  cancelPendingLink: () => {
    throw new Error('AuthProvider not found');
  },
  getSignInMethodsForEmail: async () => {
    throw new Error('AuthProvider not found');
  },
  markAsOnboarded: async () => {
    throw new Error('AuthProvider not found');
  },
};

/**
 * Auth context
 */
export const AuthContext = createContext<AuthContextValue>(defaultContextValue);

/**
 * Props for AuthProvider component
 */
export interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider component
 *
 * Wraps the application and provides authentication state and methods
 * to all child components via React Context.
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingLink, setPendingLink] = useState<PendingLinkInfo | null>(null);

  // Subscribe to auth state changes
  useEffect(() => {
    const auth = getFirebaseAuth();
    let profileUnsubscribe: Unsubscribe | null = null;

    const unsubscribe = onAuthStateChanged(
      auth,
      (firebaseUser) => {
        setUser(firebaseUser);

        // Clear user profile and unsubscribe when signed out
        if (!firebaseUser) {
          setUserProfile(null);
          setLoading(false);
          if (profileUnsubscribe) {
            profileUnsubscribe();
            profileUnsubscribe = null;
          }
          return;
        }

        // Subscribe to user profile document for real-time updates (including isAdmin)
        const db = getFirebaseDb();
        const userDocRef = doc(db, 'users', firebaseUser.uid);

        profileUnsubscribe = onSnapshot(
          userDocRef,
          (docSnapshot) => {
            if (docSnapshot.exists()) {
              const data = docSnapshot.data();
              const profile: User = {
                id: docSnapshot.id,
                googleId: (data['googleId'] as string) ?? '',
                email: (data['email'] as string) ?? firebaseUser.email ?? '',
                displayName: (data['displayName'] as string) ?? firebaseUser.displayName ?? '',
                photoURL: (data['photoURL'] as string | null) ?? firebaseUser.photoURL ?? null,
                isAdmin: (data['isAdmin'] as boolean) ?? false,
                isOnboarded: (data['isOnboarded'] as boolean) ?? false,
                createdAt: data['createdAt'],
                updatedAt: data['updatedAt'],
              };
              setUserProfile(profile);
            } else {
              // User document doesn't exist yet (new user)
              setUserProfile(null);
            }
            setLoading(false);
          },
          (profileError) => {
            console.error('User profile subscription error:', profileError);
            // Don't set error for profile loading - user can still use the app
            setLoading(false);
          }
        );
      },
      (authError) => {
        console.error('Auth state change error:', authError);
        setError(authError.message);
        setLoading(false);
      }
    );

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribe();
      if (profileUnsubscribe) {
        profileUnsubscribe();
      }
    };
  }, []);

  /**
   * Sign in with Google popup
   */
  const signIn = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const auth = getFirebaseAuth();
      const provider = new GoogleAuthProvider();

      // Add scopes for email and profile
      provider.addScope('email');
      provider.addScope('profile');

      // Configure prompt to always show account chooser
      provider.setCustomParameters({
        prompt: 'select_account',
      });

      const result = await signInWithPopup(auth, provider);

      // If there was a pending credential to link, link it now
      if (pendingLink && result.user) {
        try {
          await linkWithCredential(result.user, pendingLink.credential);
          setPendingLink(null);
        } catch (linkError) {
          console.error('Failed to link credential:', linkError);
          // Don't fail the sign-in, just log the error
        }
      }
    } catch (signInError) {
      const code = (signInError as { code?: string }).code;

      // Handle account exists with different credential
      if (code === 'auth/account-exists-with-different-credential') {
        const authError = signInError as {
          customData?: { email?: string };
        };
        const email = authError.customData?.email;

        if (email) {
          const auth = getFirebaseAuth();
          const methods = await fetchSignInMethodsForEmail(auth, email);
          const credential = GoogleAuthProvider.credentialFromError(
            signInError as Parameters<typeof GoogleAuthProvider.credentialFromError>[0]
          );

          if (credential) {
            setPendingLink({
              email,
              credential,
              existingMethods: methods,
            });
            setError(
              `An account already exists with ${email}. Please sign in with your password first to link your Google account.`
            );
            throw signInError;
          }
        }
      }

      const errorMessage = signInError instanceof Error ? signInError.message : 'Failed to sign in';
      console.error('Sign in error:', signInError);
      setError(errorMessage);
      throw signInError;
    } finally {
      setLoading(false);
    }
  }, [pendingLink]);

  /**
   * Sign in with email and password
   */
  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      try {
        setError(null);
        setLoading(true);

        const auth = getFirebaseAuth();
        const result = await signInWithEmailAndPassword(auth, email, password);

        // If there was a pending credential to link, link it now
        if (pendingLink && result.user) {
          try {
            await linkWithCredential(result.user, pendingLink.credential);
            setPendingLink(null);
          } catch (linkError) {
            console.error('Failed to link credential:', linkError);
            // Don't fail the sign-in, just log the error
          }
        }
      } catch (signInError) {
        // Map Firebase error codes to user-friendly messages
        let errorMessage = 'Failed to sign in';
        if (signInError instanceof Error) {
          const code = (signInError as { code?: string }).code;
          switch (code) {
            case 'auth/invalid-email':
              errorMessage = 'Invalid email address';
              break;
            case 'auth/user-not-found':
              errorMessage =
                'No account found with this email. Try signing in with Google instead.';
              break;
            case 'auth/wrong-password':
              errorMessage = 'Incorrect password';
              break;
            case 'auth/invalid-credential':
              errorMessage = 'Invalid email or password';
              break;
            default:
              errorMessage = signInError.message;
          }
        }
        console.error('Sign in error:', signInError);
        setError(errorMessage);
        throw signInError;
      } finally {
        setLoading(false);
      }
    },
    [pendingLink]
  );

  /**
   * Sign up with email and password
   */
  const signUpWithEmail = useCallback(
    async (email: string, password: string, displayName: string) => {
      try {
        setError(null);
        setLoading(true);

        const auth = getFirebaseAuth();
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        // Update the user profile with display name
        await updateProfile(userCredential.user, { displayName });
      } catch (signUpError) {
        // Map Firebase error codes to user-friendly messages
        let errorMessage = 'Failed to create account';
        if (signUpError instanceof Error) {
          const code = (signUpError as { code?: string }).code;
          switch (code) {
            case 'auth/email-already-in-use': {
              // Check what providers exist for this email to give a helpful message
              try {
                const auth = getFirebaseAuth();
                const methods = await fetchSignInMethodsForEmail(auth, email);
                if (methods.includes('google.com')) {
                  errorMessage =
                    'An account with this email already exists. Please sign in with Google instead.';
                } else if (methods.length > 0) {
                  errorMessage =
                    'An account with this email already exists. Try signing in instead.';
                } else {
                  // No methods found (edge case) - might be a Google account
                  errorMessage =
                    'An account with this email already exists. It may be linked to a Google account.';
                }
              } catch {
                // If we can't fetch methods, give a generic but helpful message
                errorMessage =
                  'An account with this email already exists. It may be linked to a Google account.';
              }
              break;
            }
            case 'auth/invalid-email':
              errorMessage = 'Invalid email address';
              break;
            case 'auth/weak-password':
              errorMessage = 'Password must be at least 6 characters';
              break;
            default:
              errorMessage = signUpError.message;
          }
        }
        console.error('Sign up error:', signUpError);
        setError(errorMessage);
        throw signUpError;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Sign out the current user
   */
  const signOut = useCallback(async () => {
    try {
      setError(null);

      const auth = getFirebaseAuth();
      await firebaseSignOut(auth);

      setUserProfile(null);
    } catch (signOutError) {
      const errorMessage =
        signOutError instanceof Error ? signOutError.message : 'Failed to sign out';
      console.error('Sign out error:', signOutError);
      setError(errorMessage);
      throw signOutError;
    }
  }, []);

  /**
   * Clear any auth errors
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Link pending credential to current user
   */
  const linkPendingCredential = useCallback(async () => {
    if (!pendingLink || !user) {
      throw new Error('No pending credential to link or user not signed in');
    }

    try {
      setError(null);
      await linkWithCredential(user, pendingLink.credential);
      setPendingLink(null);
    } catch (linkError) {
      const errorMessage =
        linkError instanceof Error ? linkError.message : 'Failed to link account';
      console.error('Link credential error:', linkError);
      setError(errorMessage);
      throw linkError;
    }
  }, [pendingLink, user]);

  /**
   * Cancel pending link operation
   */
  const cancelPendingLink = useCallback(() => {
    setPendingLink(null);
    setError(null);
  }, []);

  /**
   * Get sign-in methods for an email
   */
  const getSignInMethodsForEmail = useCallback(async (email: string): Promise<string[]> => {
    const auth = getFirebaseAuth();
    return fetchSignInMethodsForEmail(auth, email);
  }, []);

  /**
   * Mark the current user as onboarded
   */
  const markAsOnboarded = useCallback(async () => {
    if (!user) {
      throw new Error('No user signed in');
    }

    try {
      const db = getFirebaseDb();
      const userDocRef = doc(db, 'users', user.uid);
      // Use setDoc with merge to create the document if it doesn't exist
      await setDoc(
        userDocRef,
        {
          isOnboarded: true,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (updateError) {
      console.error('Error marking user as onboarded:', updateError);
      throw updateError;
    }
  }, [user]);

  /**
   * Derive isAdmin from user profile
   */
  const isAdmin = userProfile?.isAdmin ?? false;

  /**
   * Memoized context value
   */
  const contextValue = useMemo<AuthContextValue>(
    () => ({
      user,
      userProfile,
      loading,
      error,
      isAdmin,
      pendingLink,
      signIn,
      signInWithEmail,
      signUpWithEmail,
      signOut,
      clearError,
      linkPendingCredential,
      cancelPendingLink,
      getSignInMethodsForEmail,
      markAsOnboarded,
    }),
    [
      user,
      userProfile,
      loading,
      error,
      isAdmin,
      pendingLink,
      signIn,
      signInWithEmail,
      signUpWithEmail,
      signOut,
      clearError,
      linkPendingCredential,
      cancelPendingLink,
      getSignInMethodsForEmail,
      markAsOnboarded,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
